import { EmptyState, PrimaryButton, Space, Text } from '@procurify/ui'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { type LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import messages from './messages'
import { EntityNameTextStyled } from './styles/EntityNameTextStyled'
import { LineClampStyled } from './styles/LineClampStyled'
import { NoneTextStyled } from './styles/NoneTextStyled'

const CELL_MAX_LINES = 2

export interface ILegalEntityListViewProps {
  entities: LegalEntity[]
  totalCount: number
  tabCount: number
  hasSearch: boolean
  isFirstRunEmpty: boolean
  hasLoadFailed?: boolean
  isActive: boolean
  isLoading?: boolean
  onAdd: () => void
  onEdit: (entity: LegalEntity) => void
  onSelect: (entity: LegalEntity) => void
  onDeactivate: (entity: LegalEntity) => void
  onActivate: (entity: LegalEntity) => void
}

export const LegalEntityListView = ({
  entities,
  totalCount,
  tabCount,
  hasSearch,
  isFirstRunEmpty,
  hasLoadFailed = false,
  isActive,
  isLoading = false,
  onAdd,
  onEdit,
  onSelect,
  onDeactivate,
  onActivate,
}: ILegalEntityListViewProps) => {
  const intl = useProcurifyIntl()

  if (hasLoadFailed) {
    return (
      <EmptyState
        context='information'
        header={intl.formatMessage(messages.loadErrorTitle)}
        content={intl.formatMessage(messages.loadErrorMessage)}
      />
    )
  }

  if (isFirstRunEmpty) {
    return (
      <EmptyState
        context='information'
        content={intl.formatMessage(messages.firstRunEmptyDescription)}
        button={
          <PrimaryButton icon='add' onClick={onAdd}>
            {intl.formatMessage(messages.newEntityButton)}
          </PrimaryButton>
        }
      />
    )
  }

  const noneAdded = (
    <NoneTextStyled>{intl.formatMessage(messages.noneAdded)}</NoneTextStyled>
  )

  // Column widths from the Figma "Breakpoints" frame (1280px variant):
  // Entity name / External ID are wider; Base currency / Country are narrower.
  // Locking `width` (rather than `maxWidth`) prevents the columns from
  // re-flowing when row content changes between pages or tabs.
  //
  // Text-bearing cells wrap to two lines, then truncate with an ellipsis;
  // the native `title` attribute surfaces the full value on hover. Matches
  // the pattern from the Bill items / APDescriptionCell table.
  const columns: DatatableColumnConfig<any, LegalEntity>[] = [
    col('BASIC', {
      label: intl.formatMessage(messages.columnName),
      width: 278,
      getter: (entity) => (
        <LineClampStyled $maxLines={CELL_MAX_LINES} title={entity.name}>
          <EntityNameTextStyled>{entity.name}</EntityNameTextStyled>
        </LineClampStyled>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnBaseCurrency),
      width: 168,
      getter: (entity) => entity.baseCurrencyName || noneAdded,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnCountry),
      width: 168,
      getter: (entity) =>
        entity.country ? (
          <LineClampStyled $maxLines={CELL_MAX_LINES} title={entity.country}>
            {entity.country}
          </LineClampStyled>
        ) : (
          noneAdded
        ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnExternalId),
      width: 278,
      getter: (entity) =>
        entity.externalId ? (
          <LineClampStyled $maxLines={CELL_MAX_LINES} title={entity.externalId}>
            {entity.externalId}
          </LineClampStyled>
        ) : (
          noneAdded
        ),
    }),
  ]

  const getMenuListItems = (entity: LegalEntity) => {
    if (entity.active) {
      return [
        {
          icon: 'edit' as const,
          text: intl.formatMessage(messages.actionEdit),
          // TODO(PLAT-4858): open edit modal.
          onClick: () => onEdit(entity),
        },
        {
          icon: 'disable' as const,
          text: intl.formatMessage(messages.actionDeactivate),
          // TODO(PLAT-4860): open deactivate confirmation.
          onClick: () => onDeactivate(entity),
        },
      ]
    }
    return [
      {
        icon: 'rotate' as const,
        text: intl.formatMessage(messages.actionActivate),
        // TODO(PLAT-4860): reactivate entity.
        onClick: () => onActivate(entity),
      },
    ]
  }

  const emptyStateText = intl.formatMessage(
    hasSearch
      ? messages.emptySearchTitle
      : isActive
        ? messages.emptyActiveTitle
        : messages.emptyInactiveTitle
  )

  const countMessage = hasSearch
    ? isActive
      ? messages.countActiveSearch
      : messages.countInactiveSearch
    : isActive
      ? messages.countActive
      : messages.countInactive

  const countValues = hasSearch
    ? { matched: totalCount, total: tabCount }
    : { count: totalCount }

  return (
    <Space size='large'>
      <Space direction='horizontal' justify='space-between' align='center'>
        <Space.Item width={280}>
          <PaginationControls.Search
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
          />
        </Space.Item>

        <PrimaryButton icon='add' onClick={onAdd}>
          {intl.formatMessage(messages.newEntityButton)}
        </PrimaryButton>
      </Space>

      <Space.Divider />

      {!isLoading && (
        <Text bold>{intl.formatMessage(countMessage, countValues)}</Text>
      )}

      <DataTable<LegalEntity>
        id='legal-entity-list-table'
        aria-label={intl.formatMessage(messages.tableAriaLabel)}
        data={entities}
        isLoading={isLoading}
        customizableColumns={false}
        persistCustomizedColumns={false}
        columns={columns}
        emptyStateText={emptyStateText}
        emptyStateContext='information'
        menuListItems={getMenuListItems}
        onRowClick={onSelect}
        hoverable
      />

      <Space direction='horizontal' justify='space-between' align='center'>
        <PaginationControls.PageLimit />
        <PaginationControls.Page />
      </Space>
    </Space>
  )
}

LegalEntityListView.displayName = 'LegalEntityListView'
